import {Settings} from "@mui/icons-material";
import {List, ListItemButton, ListItemIcon, ListItemText} from "@mui/material";
import {AuditIcon} from "@variocube/app-ui";
import React, {ReactElement} from "react";
import {Link as RouterLink, useMatch} from "react-router-dom";
import {useLocalization, useWordingFlavor} from "@smalox/shared";
import {useTenantId, useTenantPermission, useTenantPermissions, useTenantPermissionsFlat} from "../tenant";
import {
	BookingIcon,
	CompartmentIcon,
	DashboardIcon,
	GroupIcon,
	LocationIcon,
	LockScanIcon,
	PlanIcon,
	StatisticsIcon,
	SubscriptionIcon,
	UsersIcon,
} from "../icons";
import {useUser} from "../user";

export function SideNav() {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const tenantId = useTenantId();
	const tenantPermissions = useTenantPermissionsFlat();
	const settingsEdit = useTenantPermission("SETTINGS_EDIT");
	const users = useTenantPermission("TENANT_USER_READ");
	const groups = useTenantPermission("GROUP_READ");
	const audit = useTenantPermission("AUDIT_LOG_READ");
	const statistics = useTenantPermission("STATISTICS_READ");
	const dashboard = useTenantPermissions("BOOKING_READ", "TENANT_USER_READ");

	const onlyHasBookingCreateOwn = tenantPermissions.length === 0
		|| (tenantPermissions.length === 1 && tenantPermissions[0] === "BOOKING_CREATE_OWN");
	const lockSearch = !onlyHasBookingCreateOwn;

	const user = useUser();

	return (
		<List>
			{dashboard && (
				<MainMenuItem
					label={t("dashboard.title")}
					icon={<DashboardIcon />}
					path={`/${tenantId}/dashboard`}
					exact
				/>
			)}
			<MainMenuItem
				label={t(`bookings.title.${wordingFlavor}`)}
				icon={<BookingIcon />}
				path={`/${tenantId}/bookings`}
			/>
			<MainMenuItem
				label={t(`compartments.title.${wordingFlavor}`)}
				icon={<CompartmentIcon />}
				path={`/${tenantId}/compartments`}
			/>
			{users && (
				<MainMenuItem
					label={t("users.title")}
					icon={<UsersIcon />}
					path={`/${tenantId}/users`}
				/>
			)}
			<MainMenuItem
				label={t("locations.title")}
				icon={<LocationIcon />}
				path={`/${tenantId}/locations`}
			/>
			{statistics && (
				<MainMenuItem
					label={t("statistics.title")}
					icon={<StatisticsIcon />}
					path={`/${tenantId}/statistics`}
				/>
			)}
			{groups && (
				<MainMenuItem
					label={t("groups.title")}
					icon={<GroupIcon />}
					path={`/${tenantId}/groups`}
				/>
			)}
			{audit && (
				<MainMenuItem
					label={t("audit.title")}
					icon={<AuditIcon />}
					path={`/${tenantId}/audit`}
				/>
			)}
			{lockSearch && (
				<MainMenuItem
					label={t("lockSearch.title")}
					icon={<LockScanIcon />}
					path={`/${tenantId}/locksearch`}
				/>
			)}
			{user.superUser && (
				<MainMenuItem
					label={t("plans.title")}
					icon={<PlanIcon />}
					path={`/${tenantId}/plans`}
				/>
			)}
			{user.superUser && (
				<MainMenuItem
					label={t("subscriptions.title")}
					icon={<SubscriptionIcon />}
					path={`/${tenantId}/subscriptions`}
				/>
			)}
			{settingsEdit && (
				<MainMenuItem
					label={t("settings.title")}
					icon={<Settings />}
					path={`/${tenantId}/settings`}
				/>
			)}
		</List>
	);
}

interface MainMenuItemProps {
	label: string;
	path?: string;
	exact?: boolean;
	icon: ReactElement;
	onClick?: () => void;
}

function MainMenuItem({label, path, exact = false, icon, onClick}: Readonly<MainMenuItemProps>) {
	const match = useMatch({
		path: path ?? "__path_that_cannot_match",
		end: exact,
	});

	const otherProps = path ? {component: RouterLink, to: path} : {onClick};

	return (
		<ListItemButton selected={Boolean(match)} {...otherProps}>
			<ListItemIcon>
				{icon}
			</ListItemIcon>
			<ListItemText
				primary={label}
				sx={{
					whiteSpace: "normal",
					wordBreak: "normal",
				}}
			/>
		</ListItemButton>
	);
}
